import * as THREE from 'three';
import {
  FeatureFace,
  FeatureLine,
  FeaturePoint,
  isFeatureFace,
  isFeatureSurface,
  isFeatureLine,
  isFeaturePoint,
} from '../../mesh/feature';
import { buildCircleFromThreePoints2D } from '../../tool/buildCircleFromThreePoints2D';
import { resolveFeatureSurfaceAxis } from '../../reference/reference-axis/resolveFeatureSurfaceAxis';
import { resolveOwningFeatureResultMesh } from './resolveFeatureSceneMesh';

/** 几何退化容差。 */
const GEOMETRY_EPSILON = 1e-8;

/** 几何平面距离校验容差。 */
const PLANAR_WRAPPED_SURFACE_DISTANCE_TOLERANCE = 1e-5;

/** 圆边描述符最多保留的采样点数量。 */
const CIRCULAR_EDGE_SAMPLE_LIMIT = 24;

/** 圆柱半径估算最多读取的面顶点数量。 */
const CYLINDRICAL_RADIUS_SAMPLE_LIMIT = 1024;

/** 圆柱半径分簇的相对容差。 */
const CYLINDRICAL_RADIUS_CLUSTER_RELATIVE_TOLERANCE = 1e-4;

/**
 * 将 Three.js 向量转换为可序列化三维元组。
 *
 * @param vector Three.js 向量。
 * @returns 三维坐标元组。
 */
function toPointTuple(vector: THREE.Vector3): TopologyPoint3D {
  return [vector.x, vector.y, vector.z];
}

/**
 * 归一化向量。
 *
 * @param vector 源向量。
 * @returns 单位向量；退化时返回 null。
 */
function normalizeVector(vector: THREE.Vector3): THREE.Vector3 | null {
  if (vector.lengthSq() <= GEOMETRY_EPSILON) {
    return null;
  }
  return vector.clone().normalize();
}

/**
 * 为给定法向构建稳定二维基。
 *
 * @param normal 圆所在平面法向。
 * @returns 二维局部基；法向退化时返回 null。
 */
function buildPlaneBasis(normal: THREE.Vector3): { u: THREE.Vector3; v: THREE.Vector3 } | null {
  const normalizedNormal = normalizeVector(normal);
  if (!normalizedNormal) {
    return null;
  }

  const seed = Math.abs(normalizedNormal.x) < 0.9
    ? new THREE.Vector3(1, 0, 0)
    : new THREE.Vector3(0, 1, 0);
  const u = seed.sub(normalizedNormal.clone().multiplyScalar(seed.dot(normalizedNormal)));
  const normalizedU = normalizeVector(u);
  if (!normalizedU) {
    return null;
  }
  return {
    u: normalizedU,
    v: normalizedNormal.clone().cross(normalizedU).normalize(),
  };
}

/**
 * 读取拓扑对象的公共身份字段。
 *
 * @param object 点、线、面代理对象。
 * @returns 公共身份字段。
 */
function resolveCommonDescriptorFields(object: FeatureFace | FeatureLine | FeaturePoint): {
  bodyObjectId: string;
  topologyIndex: number;
  subEntityId?: string;
  persistentRef?: string;
} | null {
  if (isFeatureFace(object)) {
    return {
      bodyObjectId: object.objectId,
      topologyIndex: object.faceIndex,
      subEntityId: object.subEntityId,
      persistentRef: object.persistentRef,
    };
  }

  if (isFeatureLine(object) && object.objectId && typeof object.edgeIndex === 'number') {
    return {
      bodyObjectId: object.objectId,
      topologyIndex: object.edgeIndex,
      subEntityId: object.subEntityId ?? undefined,
      persistentRef: object.persistentRef ?? undefined,
    };
  }

  if (isFeaturePoint(object) && object.objectId && typeof object.vertexIndex === 'number') {
    return {
      bodyObjectId: object.objectId,
      topologyIndex: object.vertexIndex,
      subEntityId: object.subEntityId ?? undefined,
      persistentRef: object.persistentRef ?? undefined,
    };
  }

  return null;
}

/**
 * 读取面的底层曲面类型。
 *
 * @param face 面代理对象。
 * @returns 曲面类型。
 */
function resolveSurfaceKind(face: FeatureFace): TopologySurfaceKind | 'unknown' {
  const value = Reflect.get(face, 'surfaceKind');
  if (typeof value === 'string' && value.length > 0) {
    return value as TopologySurfaceKind;
  }

  /*
   * 基础 FeatureFace 的语义就是平面面。
   * 非平面解析曲面会由 FeatureSurface 显式携带 surfaceKind，不能让基础面退化成 unknown。
   */
  return 'plane';
}

/**
 * 判断解析曲面类型是否可能只是几何平面的包装。
 *
 * @param surfaceKind 拓扑曲面类型。
 * @returns 是否允许通过几何共面校验恢复为平面。
 */
function canRecoverWrappedPlanarSurface(surfaceKind: TopologySurfaceKind | 'unknown'): boolean {
  return surfaceKind === 'surface-of-revolution' || surfaceKind === 'surface-of-extrusion';
}

/**
 * 判断面代理的顶点是否全部落在指定世界平面上。
 *
 * 设计说明：
 * 1. 历史导入或布尔结果可能把几何平面包成 `surface-of-revolution/extrusion`；
 * 2. 不能仅凭曲面类型降级，否则真实旋转/拉伸曲面会被错误当成平面；
 * 3. 因此只在代理几何所有顶点都与权威平面共面时恢复平面语义。
 *
 * @param face 面代理对象。
 * @param plane 面代理解析出的世界平面。
 * @returns 是否是可恢复的几何平面。
 */
function isFaceGeometryCoplanarWithPlane(
  face: FeatureFace,
  plane: { point: THREE.Vector3; normal: THREE.Vector3 },
): boolean {
  const normal = normalizeVector(plane.normal);
  if (!normal) {
    return false;
  }

  const positionAttribute = face.geometry.getAttribute('position');
  if (!(positionAttribute instanceof THREE.BufferAttribute) || positionAttribute.count <= 0) {
    return false;
  }

  face.updateWorldMatrix(true, false);
  const indexAttribute = face.geometry.getIndex();
  const vertexCount = indexAttribute instanceof THREE.BufferAttribute
    ? indexAttribute.count
    : positionAttribute.count;
  let hasValidPoint = false;
  const localPoint = new THREE.Vector3();
  const worldPoint = new THREE.Vector3();

  for (let cursor = 0; cursor < vertexCount; cursor += 1) {
    const vertexIndex = indexAttribute instanceof THREE.BufferAttribute
      ? Number(indexAttribute.getX(cursor))
      : cursor;
    if (!Number.isInteger(vertexIndex) || vertexIndex < 0 || vertexIndex >= positionAttribute.count) {
      continue;
    }

    localPoint.set(
      positionAttribute.getX(vertexIndex),
      positionAttribute.getY(vertexIndex),
      positionAttribute.getZ(vertexIndex),
    );
    worldPoint.copy(localPoint).applyMatrix4(face.matrixWorld);
    const signedDistance = worldPoint.sub(plane.point).dot(normal);
    if (Math.abs(signedDistance) > PLANAR_WRAPPED_SURFACE_DISTANCE_TOLERANCE) {
      return false;
    }
    hasValidPoint = true;
  }

  return hasValidPoint;
}

/**
 * 尝试把被解析曲面包装的几何平面恢复为平面描述符。
 *
 * @param face 面代理对象。
 * @param common 公共拓扑身份字段。
 * @param surfaceKind 当前曲面类型。
 * @returns 平面描述符；不满足恢复条件时返回 `null`。
 */
function resolveWrappedPlanarSurfaceDescriptor(
  face: FeatureFace,
  common: NonNullable<ReturnType<typeof resolveCommonDescriptorFields>>,
  surfaceKind: TopologySurfaceKind | 'unknown',
): BRepGeometryDescriptor | null {
  if (!canRecoverWrappedPlanarSurface(surfaceKind)) {
    return null;
  }

  const plane = face.getWorldPlane();
  if (!isFaceGeometryCoplanarWithPlane(face, plane)) {
    return null;
  }

  return {
    ...common,
    entityKind: 'face',
    coordinateSpace: 'world',
    surfaceKind: 'plane',
    point: toPointTuple(plane.point),
    normal: toPointTuple(plane.normal),
  };
}

/**
 * 读取边的底层曲线类型。
 *
 * @param line 边代理对象。
 * @returns 曲线类型。
 */
function resolveCurveKind(line: FeatureLine): TopologyCurveKind | 'unknown' {
  const value = Reflect.get(line, 'curveKind');
  if (typeof value === 'string' && value.length > 0) {
    return value as TopologyCurveKind;
  }

  /*
   * 基础 FeatureLine 的语义就是直线边。
   * 非直线解析曲线会由 FeatureCurve 显式携带 curveKind，保持对象边界和几何描述一致。
   */
  return 'line';
}

/**
 * 读取边的采样点。
 *
 * @param line 边代理对象。
 * @returns 采样点集合；不存在时返回 `undefined`。
 */
function resolveEdgeSampledPoints(line: FeatureLine): TopologyPoint3D[] | undefined {
  if (typeof line.edgeIndex !== 'number') {
    return undefined;
  }
  const owner = resolveOwningFeatureResultMesh(line);
  const edge = owner?.getTopologyEdgeSnapshotByIndex(line.edgeIndex);
  return edge?.sampledPoints?.map((point) => [point[0], point[1], point[2]]);
}

/**
 * 读取线对象几何中的世界坐标采样点。
 *
 * @param line 线或曲线代理对象。
 * @returns 世界坐标采样点。
 */
function readLineWorldSamplePoints(line: FeatureLine): THREE.Vector3[] {
  const worldSamplePoints = line.getWorldSamplePoints();
  if (worldSamplePoints.length < 3) {
    return [];
  }

  const points: THREE.Vector3[] = [];
  worldSamplePoints.forEach((point) => {
    const previous = points[points.length - 1] ?? null;
    if (!previous || previous.distanceToSquared(point) > GEOMETRY_EPSILON) {
      points.push(point);
    }
  });

  if (points.length >= 2 && points[0].distanceToSquared(points[points.length - 1]) <= GEOMETRY_EPSILON) {
    points.pop();
  }
  return points;
}

/**
 * 从采样点中选择可拟合圆的三点。
 *
 * @param points 世界坐标采样点。
 * @returns 稳定三点；无法找到时返回 null。
 */
function resolveCircleSolvePoints(points: readonly THREE.Vector3[]): readonly [THREE.Vector3, THREE.Vector3, THREE.Vector3] | null {
  if (points.length < 3) {
    return null;
  }

  const first = points[0];
  for (let middleIndex = 1; middleIndex < points.length - 1; middleIndex += 1) {
    const middle = points[middleIndex];
    for (let lastIndex = points.length - 1; lastIndex > middleIndex; lastIndex -= 1) {
      const last = points[lastIndex];
      const normal = middle.clone().sub(first).cross(last.clone().sub(first));
      if (normal.lengthSq() > GEOMETRY_EPSILON) {
        return [first.clone(), middle.clone(), last.clone()] as const;
      }
    }
  }
  return null;
}

/**
 * 从圆采样点解析圆心、法向和半径。
 *
 * @param points 世界坐标采样点。
 * @returns 圆参数；无法稳定解析时返回 null。
 */
function resolveCircleGeometry(points: readonly THREE.Vector3[]): {
  center: THREE.Vector3;
  normal: THREE.Vector3;
  radius: number;
} | null {
  const solvePoints = resolveCircleSolvePoints(points);
  if (!solvePoints) {
    return null;
  }

  const [first, second, third] = solvePoints;
  const normal = normalizeVector(second.clone().sub(first).cross(third.clone().sub(first)));
  if (!normal) {
    return null;
  }

  const basis = buildPlaneBasis(normal);
  if (!basis) {
    return null;
  }

  const localSecond = second.clone().sub(first);
  const localThird = third.clone().sub(first);
  const circle = buildCircleFromThreePoints2D(
    new THREE.Vector2(0, 0),
    new THREE.Vector2(localSecond.dot(basis.u), localSecond.dot(basis.v)),
    new THREE.Vector2(localThird.dot(basis.u), localThird.dot(basis.v)),
  );
  if (!circle || !Number.isFinite(circle.radius) || circle.radius <= GEOMETRY_EPSILON) {
    return null;
  }

  return {
    center: first.clone()
      .add(basis.u.clone().multiplyScalar(circle.center.x))
      .add(basis.v.clone().multiplyScalar(circle.center.y)),
    normal,
    radius: circle.radius,
  };
}

/**
 * 按固定上限复制采样点，避免选择事件携带过大数组。
 *
 * @param points 原始采样点。
 * @returns 可序列化采样点。
 */
function serializeLimitedSamplePoints(points: readonly THREE.Vector3[]): TopologyPoint3D[] {
  if (points.length <= CIRCULAR_EDGE_SAMPLE_LIMIT) {
    return points.map(toPointTuple);
  }
  const step = Math.max(1, Math.floor(points.length / CIRCULAR_EDGE_SAMPLE_LIMIT));
  const sampled: TopologyPoint3D[] = [];
  for (let index = 0; index < points.length && sampled.length < CIRCULAR_EDGE_SAMPLE_LIMIT; index += step) {
    sampled.push(toPointTuple(points[index]));
  }
  return sampled;
}

/**
 * 计算点到轴线的距离。
 *
 * @param point 待测点。
 * @param axisOrigin 轴线上一点。
 * @param axisDirection 轴向。
 * @returns 点到轴的垂直距离。
 */
function resolvePointToAxisDistance(
  point: THREE.Vector3,
  axisOrigin: THREE.Vector3,
  axisDirection: THREE.Vector3,
): number {
  const originToPoint = point.clone().sub(axisOrigin);
  const projected = axisOrigin.clone().addScaledVector(axisDirection, originToPoint.dot(axisDirection));
  return point.distanceTo(projected);
}

/**
 * 读取面代理几何顶点的世界坐标采样。
 *
 * 设计说明：
 * 1. 圆柱面代理的 `getWorldPoint()` 对完整圆柱侧面通常落在轴线上，只能作为面代表点，不能作为曲面采样点；
 * 2. 半径必须来自当前被选中的面几何顶点，避免把圆柱中心、孔中心或其它非曲面点混入相切求解；
 * 3. 这里在描述符生成阶段运行，不在 pointermove 热路径中执行，因此允许一次性读取有限采样。
 *
 * @param face 面代理对象。
 * @returns 世界坐标采样点。
 */
function collectFaceGeometryWorldSamplePoints(face: FeatureFace): THREE.Vector3[] {
  const positionAttribute = face.geometry.getAttribute('position');
  if (!(positionAttribute instanceof THREE.BufferAttribute) || positionAttribute.count <= 0) {
    return [];
  }

  face.updateWorldMatrix(true, false);
  const indexAttribute = face.geometry.getIndex();
  const indexedVertexCount = indexAttribute instanceof THREE.BufferAttribute
    ? indexAttribute.count
    : positionAttribute.count;
  const step = Math.max(1, Math.floor(indexedVertexCount / CYLINDRICAL_RADIUS_SAMPLE_LIMIT));
  const seenVertexIndices = new Set<number>();
  const points: THREE.Vector3[] = [];

  for (let cursor = 0; cursor < indexedVertexCount; cursor += step) {
    const vertexIndex = indexAttribute instanceof THREE.BufferAttribute
      ? Number(indexAttribute.getX(cursor))
      : cursor;
    if (seenVertexIndices.has(vertexIndex) || vertexIndex < 0 || vertexIndex >= positionAttribute.count) {
      continue;
    }
    seenVertexIndices.add(vertexIndex);
    points.push(
      new THREE.Vector3(
        positionAttribute.getX(vertexIndex),
        positionAttribute.getY(vertexIndex),
        positionAttribute.getZ(vertexIndex),
      ).applyMatrix4(face.matrixWorld),
    );
  }

  return points;
}

/**
 * 从半径候选值中取主半径簇。
 *
 * 设计说明：
 * 1. imported sidecar 的面代理可能携带重复顶点、边界顶点或极少量退化点；
 * 2. 直接平均会被轴心点拉小，直接取最大值又容易被离群点拉大；
 * 3. 因此按数值接近度分簇，取样本最多的主簇中位数，稳定表达当前解析圆柱面的真实半径。
 *
 * @param distances 点到轴线距离候选。
 * @returns 稳健半径；无有效候选时返回 undefined。
 */
function resolveDominantRadiusFromDistances(distances: number[]): number | undefined {
  const sortedDistances = distances
    .filter((distance) => Number.isFinite(distance) && distance > GEOMETRY_EPSILON)
    .sort((left, right) => left - right);
  if (sortedDistances.length === 0) {
    return undefined;
  }

  const medianDistance = sortedDistances[Math.floor(sortedDistances.length / 2)];
  const clusterTolerance = Math.max(
    GEOMETRY_EPSILON * 10,
    Math.abs(medianDistance) * CYLINDRICAL_RADIUS_CLUSTER_RELATIVE_TOLERANCE,
  );
  const clusters: number[][] = [];
  let currentCluster: number[] = [];
  sortedDistances.forEach((distance) => {
    const previousDistance = currentCluster[currentCluster.length - 1];
    if (currentCluster.length === 0 || Math.abs(distance - previousDistance) <= clusterTolerance) {
      currentCluster.push(distance);
      return;
    }
    clusters.push(currentCluster);
    currentCluster = [distance];
  });
  if (currentCluster.length > 0) {
    clusters.push(currentCluster);
  }

  const dominantCluster = clusters.reduce((bestCluster, cluster) => {
    if (!bestCluster || cluster.length > bestCluster.length) {
      return cluster;
    }
    if (cluster.length < bestCluster.length) {
      return bestCluster;
    }

    const clusterMedian = cluster[Math.floor(cluster.length / 2)];
    const bestMedian = bestCluster[Math.floor(bestCluster.length / 2)];
    return Math.abs(clusterMedian - medianDistance) < Math.abs(bestMedian - medianDistance)
      ? cluster
      : bestCluster;
  }, null as number[] | null);
  if (!dominantCluster || dominantCluster.length === 0) {
    return medianDistance;
  }

  return dominantCluster[Math.floor(dominantCluster.length / 2)];
}

/**
 * 从当前圆柱曲面几何估算半径。
 *
 * @param face 面代理对象。
 * @param axisOrigin 轴上一点。
 * @param axisDirection 轴向。
 * @returns 半径；无法解析时返回 undefined。
 */
function resolveCylindricalRadius(
  face: FeatureFace,
  axisOrigin: THREE.Vector3,
  axisDirection: THREE.Vector3,
): number | undefined {
  const geometryPoints = collectFaceGeometryWorldSamplePoints(face);
  const geometryRadius = resolveDominantRadiusFromDistances(geometryPoints
    .map((point) => resolvePointToAxisDistance(point, axisOrigin, axisDirection))
  );
  if (geometryRadius !== undefined) {
    return geometryRadius;
  }

  const boundaryPoints = face.getWorldBoundaryVertices();
  return resolveDominantRadiusFromDistances(boundaryPoints
    .map((point) => resolvePointToAxisDistance(point, axisOrigin, axisDirection))
  );
}

/**
 * 解析圆锥面的半角与顶点。
 *
 * 设计说明：
 * 圆锥相切必须知道真实母线斜率，不能只把圆锥退化成中心轴线。这里只消费
 * FeatureSurface 已有的世界空间代表点和边界点，通过“轴向投影 - 到轴距离”的线性关系
 * 估算半角与顶点，保持描述符为可序列化数据，不引入运行时 Object3D 依赖。
 *
 * @param face 面代理对象。
 * @param axisOrigin 轴线上一点。
 * @param axisDirection 轴向。
 * @returns 圆锥参数；无法稳定解析时返回 null。
 */
function resolveConicalParameters(
  face: FeatureFace,
  axisOrigin: THREE.Vector3,
  axisDirection: THREE.Vector3,
): { halfAngle: number; apex: THREE.Vector3 } | null {
  const points = [
    face.getWorldPoint(),
    ...face.getWorldBoundaryVertices(),
  ];
  const measurements = points
    .map((point) => {
      const originToPoint = point.clone().sub(axisOrigin);
      const axial = originToPoint.dot(axisDirection);
      const projected = axisOrigin.clone().addScaledVector(axisDirection, axial);
      const radius = point.distanceTo(projected);
      return { axial, radius };
    })
    .filter((item) => (
      Number.isFinite(item.axial)
      && Number.isFinite(item.radius)
      && item.radius > GEOMETRY_EPSILON
    ));

  if (measurements.length < 2) {
    return null;
  }

  const axialMean = measurements.reduce((sum, item) => sum + item.axial, 0) / measurements.length;
  const radiusMean = measurements.reduce((sum, item) => sum + item.radius, 0) / measurements.length;
  const variance = measurements.reduce((sum, item) => {
    const delta = item.axial - axialMean;
    return sum + delta * delta;
  }, 0);
  if (variance <= GEOMETRY_EPSILON) {
    return null;
  }

  const covariance = measurements.reduce((sum, item) => (
    sum + (item.axial - axialMean) * (item.radius - radiusMean)
  ), 0);
  const slope = covariance / variance;
  if (!Number.isFinite(slope) || Math.abs(slope) <= GEOMETRY_EPSILON) {
    return null;
  }

  const halfAngle = Math.atan(Math.abs(slope));
  if (!Number.isFinite(halfAngle) || halfAngle <= GEOMETRY_EPSILON || halfAngle >= Math.PI / 2 - GEOMETRY_EPSILON) {
    return null;
  }

  const intercept = radiusMean - slope * axialMean;
  const apexAxial = -intercept / slope;
  if (!Number.isFinite(apexAxial)) {
    return null;
  }

  return {
    halfAngle,
    apex: axisOrigin.clone().addScaledVector(axisDirection, apexAxial),
  };
}

/**
 * 从面代理估算球面描述符。
 *
 * @param face 面代理对象。
 * @param common 公共身份字段。
 * @returns 球面描述符；无法解析时返回 null。
 */
function resolveSphereGeometryDescriptor(
  face: FeatureFace,
  common: NonNullable<ReturnType<typeof resolveCommonDescriptorFields>>,
): BRepGeometryDescriptor | null {
  const box = new THREE.Box3().setFromObject(face);
  if (box.isEmpty()) {
    return null;
  }

  const center = box.getCenter(new THREE.Vector3());
  const boundaryVertices = face.getWorldBoundaryVertices();
  const candidatePoints = boundaryVertices.length > 0 ? boundaryVertices : [box.min, box.max];
  const radius = candidatePoints.reduce(
    (maxRadius, point) => Math.max(maxRadius, point.distanceTo(center)),
    0,
  );
  if (!Number.isFinite(radius) || radius <= GEOMETRY_EPSILON) {
    return null;
  }

  return {
    ...common,
    entityKind: 'face',
    coordinateSpace: 'world',
    surfaceKind: 'sphere',
    center: toPointTuple(center),
    radius,
    point: toPointTuple(face.getWorldPoint()),
  };
}

/**
 * 解析圆柱或圆锥曲面描述符。
 *
 * @param face 面代理对象。
 * @param common 公共身份字段。
 * @returns 曲面描述符；无法解析时返回 null。
 */
function resolveAxisSurfaceGeometryDescriptor(
  face: FeatureFace,
  common: NonNullable<ReturnType<typeof resolveCommonDescriptorFields>>,
): BRepGeometryDescriptor | null {
  if (!isFeatureSurface(face)) {
    return null;
  }

  const axis = resolveFeatureSurfaceAxis(face);
  if (!axis) {
    return null;
  }
  const axisDirection = normalizeVector(axis.axisDirection);
  if (!axisDirection) {
    return null;
  }

  if (face.surfaceKind === 'cylinder') {
    return {
      ...common,
      entityKind: 'face',
      coordinateSpace: 'world',
      surfaceKind: 'cylinder',
      axisOrigin: toPointTuple(axis.axisOrigin),
      axisDirection: toPointTuple(axisDirection),
      point: toPointTuple(axis.surfacePoint),
      radius: resolveCylindricalRadius(face, axis.axisOrigin, axisDirection),
    };
  }

  if (face.surfaceKind === 'cone') {
    const conicalParameters = resolveConicalParameters(face, axis.axisOrigin, axisDirection);
    return {
      ...common,
      entityKind: 'face',
      coordinateSpace: 'world',
      surfaceKind: 'cone',
      axisOrigin: toPointTuple(axis.axisOrigin),
      axisDirection: toPointTuple(axisDirection),
      point: toPointTuple(axis.surfacePoint),
      ...(conicalParameters
        ? {
            halfAngle: conicalParameters.halfAngle,
            apex: toPointTuple(conicalParameters.apex),
          }
        : {}),
    };
  }

  return null;
}

/**
 * 解析面的 BRep 几何描述符。
 *
 * @param face 面代理对象。
 * @returns BRep 几何描述符。
 */
function resolveFaceGeometryDescriptor(face: FeatureFace): BRepGeometryDescriptor | null {
  const common = resolveCommonDescriptorFields(face);
  if (!common) {
    return null;
  }

  const surfaceKind = resolveSurfaceKind(face);
  if (surfaceKind !== 'plane') {
    const wrappedPlanarDescriptor = resolveWrappedPlanarSurfaceDescriptor(face, common, surfaceKind);
    if (wrappedPlanarDescriptor) {
      return wrappedPlanarDescriptor;
    }

    if (surfaceKind === 'cylinder' || surfaceKind === 'cone') {
      const axisDescriptor = resolveAxisSurfaceGeometryDescriptor(face, common);
      if (axisDescriptor) {
        return axisDescriptor;
      }
    }

    if (surfaceKind === 'sphere') {
      const sphereDescriptor = resolveSphereGeometryDescriptor(face, common);
      if (sphereDescriptor) {
        return sphereDescriptor;
      }
    }

    return {
      ...common,
      entityKind: 'face',
      coordinateSpace: 'world',
      surfaceKind: surfaceKind === 'cylinder' || surfaceKind === 'cone' || surfaceKind === 'sphere'
        ? 'unknown'
        : surfaceKind,
    };
  }

  const plane = face.getWorldPlane();
  return {
    ...common,
    entityKind: 'face',
    coordinateSpace: 'world',
    surfaceKind: 'plane',
    point: toPointTuple(plane.point),
    normal: toPointTuple(plane.normal),
  };
}

/**
 * 解析边的 BRep 几何描述符。
 *
 * @param line 边代理对象。
 * @returns BRep 几何描述符。
 */
function resolveEdgeGeometryDescriptor(line: FeatureLine): BRepGeometryDescriptor | null {
  const common = resolveCommonDescriptorFields(line);
  if (!common) {
    return null;
  }

  const curveKind = resolveCurveKind(line);
  if (curveKind !== 'line') {
    if (curveKind === 'circle') {
      const sampledPoints = readLineWorldSamplePoints(line);
      const circle = resolveCircleGeometry(sampledPoints);
      if (circle) {
        const endpoints = line.getWorldEndpoints();
        return {
          ...common,
          entityKind: 'edge',
          coordinateSpace: 'world',
          curveKind: 'circle',
          center: toPointTuple(circle.center),
          normal: toPointTuple(circle.normal),
          axisOrigin: toPointTuple(circle.center),
          axisDirection: toPointTuple(circle.normal),
          radius: circle.radius,
          start: endpoints ? toPointTuple(endpoints.start) : undefined,
          end: endpoints ? toPointTuple(endpoints.end) : undefined,
          sampledPoints: serializeLimitedSamplePoints(sampledPoints),
        };
      }
    }

    return {
      ...common,
      entityKind: 'edge',
      coordinateSpace: 'world',
      curveKind: curveKind === 'circle' ? 'unknown' : curveKind,
      sampledPoints: resolveEdgeSampledPoints(line),
    };
  }

  const endpoints = line.getWorldEndpoints();
  const direction = line.getWorldDirection();
  if (!endpoints || !direction) {
    return null;
  }

  return {
    ...common,
    entityKind: 'edge',
    coordinateSpace: 'world',
    curveKind: 'line',
    start: toPointTuple(endpoints.start),
    end: toPointTuple(endpoints.end),
    direction: toPointTuple(direction),
  };
}

/**
 * 解析顶点的 BRep 几何描述符。
 *
 * @param point 顶点代理对象。
 * @returns BRep 几何描述符。
 */
function resolveVertexGeometryDescriptor(point: FeaturePoint): BRepGeometryDescriptor | null {
  const common = resolveCommonDescriptorFields(point);
  if (!common) {
    return null;
  }

  return {
    ...common,
    entityKind: 'vertex',
    coordinateSpace: 'world',
    point: toPointTuple(point.getWorldPoint()),
  };
}

/**
 * 将点、线、面代理解析为配合求解可消费的 BRep 几何描述符。
 *
 * 设计说明：
 * 1. 该函数只读取现有 Feature 代理，不创建任何渲染对象，适合在选择事件热路径中调用；
 * 2. 圆柱、圆锥与圆边复用既有 Feature 代理上的解析语义，不改变 BVH 命中索引；
 * 3. 无法稳定解析的曲面/曲线会显式返回 unsupported descriptor，避免错误创建配合。
 *
 * @param object 当前选择命中的场景对象。
 * @returns BRep 几何描述符；非 BRep Feature 对象返回 `null`。
 */
export function resolveBRepGeometryDescriptor(
  object: THREE.Object3D | null | undefined,
): BRepGeometryDescriptor | null {
  if (!object) {
    return null;
  }

  if (isFeatureFace(object)) {
    return resolveFaceGeometryDescriptor(object);
  }

  if (isFeatureLine(object)) {
    return resolveEdgeGeometryDescriptor(object);
  }

  if (isFeaturePoint(object)) {
    return resolveVertexGeometryDescriptor(object);
  }

  return null;
}
